import type { Ref, UnwrapRef } from 'vue';
import { useApiError } from './useApiError';
import { useApiConfig } from "@/composables/useApiConfig";

export type AllowedMethods = 'POST' | 'GET' | 'PATCH' | 'DELETE';

export interface RequestResponse<R, P> {
    data: Ref<UnwrapRef<R | null>>,
    loading: Ref<boolean>,
    fetch: (payload?: P, query?: Query) => Promise<void>,
    refresh: (payload?: P) => Promise<void>,
    error: Ref<ReturnType<typeof useApiError> | null>
}

export interface RequestConfiguration {
    baseUrl?: string,
    initialise?: boolean
}

export type Query = Record<string, any>;

export interface RequestParameters<P> {
    url: string | ComputedRef<string>,
    method: AllowedMethods,
    payload?: P,
    query?: Query
    multipart?: boolean
}

export const useApi = <DResponse, DPayload = any>(
    parameters: RequestParameters<DPayload>,
    configuration: RequestConfiguration = {}
): RequestResponse<DResponse, DPayload> => {
    const { $toast } = useNuxtApp();
    const { api: apiConfiguration } = useApiConfig();

    const defaultConfiguration: RequestConfiguration = {
        baseUrl: apiConfiguration.baseUrl,
        initialise: false
    }

    const loading = ref<boolean>(false);
    const data = ref<UnwrapRef<DResponse> | null>(null) as Ref<UnwrapRef<DResponse | null>>;
    const error = ref<ReturnType<typeof useApiError> | null>(null);

    const config: RequestConfiguration = { ...defaultConfiguration, ...configuration }

    const fetch = async (payload?: DPayload, query?: Query): Promise<void> => {
        error.value = null;
        loading.value = true;
        try {
            let body: DPayload | FormData | undefined;

            if (parameters.method === 'POST' || parameters.method === 'PATCH') {
                const mergedPayload = {
                    ...(parameters.payload ?? {}),
                    ...(payload ?? {})
                };

                if (parameters.multipart) {
                    const formData = new FormData();
                    Object.entries(mergedPayload).forEach(([key, value]) => {
                        if (Array.isArray(value)) {
                            value.forEach(v => formData.append(key + '[]', v));
                        } else {
                            formData.append(key, value as any);
                        }
                    });
                    body = formData;
                } else {
                    body = mergedPayload as DPayload;
                }
            }

            const queryParams = { ...parameters.query, ...query };
            const queryString = Object.keys(queryParams).length >= 1 ? new URLSearchParams(queryParams).toString() : null;

            const urlValue = unref(parameters.url);
            const url = `${config.baseUrl}/${urlValue}`;

            data.value = await $fetch(
                queryString === null ? url : `${url}?${queryString}`,
                {
                    method: parameters.method,
                    body: body as BodyInit | Record<string, any> | null | undefined,
                    headers: parameters.multipart ? undefined : { 'Content-Type': 'application/json' }
                }
            );

        } catch (e: unknown) {
            error.value = useApiError(e);
            if(error.value.code === 403) {
                $toast.error(error.value.message);
            }
        }
        loading.value = false;
    };


    if(config.initialise === true) {
        fetch();
    }

    return {
        data,
        loading,
        fetch,
        refresh: fetch,
        error
    }
}